import { useContratos } from '@/hooks/useContratos'
import { useFaturas } from '@/hooks/useFaturas'
import { useAllServicos } from '@/hooks/useServicos'
import { useNoticias } from '@/hooks/useNoticias'
import { formatCurrency, formatDate, getStatusBadgeClass, translateStatus } from '@/lib/formatters'
import {
    FileText, Receipt, ArrowRight, ShieldCheck, AlertTriangle,
    Star, Sparkles, Newspaper, Info, Megaphone, ShoppingBag, Heart
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import type { Servico, NoticiaPortal } from '@/types/database'
import { useState, useRef, useEffect } from 'react'

import { usePortalStore } from '@/stores/portal.store'
import { supabase } from '@/config/supabase'

import { ContractingModal } from '@/components/ContractingModal'

import { calculateContratoStatus } from '@/lib/status-logic'

import { usePushNotifications } from '@/hooks/usePushNotifications'
import { useQuery } from '@tanstack/react-query'

export function PortalDashboardPage() {
    const navigate = useNavigate()
    usePushNotifications()
    const associado = usePortalStore(s => s.associado)
    const PORTAL_ASSOCIADO_ID = associado?.id || 'a1'
    const PORTAL_ASSOCIADO_NOME = associado?.nome?.split(' ')[0] || 'João'

    const { data: contratosResult } = useContratos({ associado_id: PORTAL_ASSOCIADO_ID, pageSize: 50 })
    const { data: faturasResult } = useFaturas({ associado_id: PORTAL_ASSOCIADO_ID, pageSize: 50 })
    const { data: allServicos } = useAllServicos()
    const { data: noticias } = useNoticias()

    /* Modal state */
    const [selectedServico, setSelectedServico] = useState<Servico | null>(null)

    const contratos = contratosResult?.data || []
    const faturas = faturasResult?.data || []
    const servicos = allServicos || []

    const contratosAtivos = contratos.filter(c => calculateContratoStatus(c, faturas) === 'ativo').length
    const proximaFatura = faturas
        .filter(f => f.status === 'pendente' || f.status === 'atrasada')
        .sort((a, b) => a.data_vencimento.localeCompare(b.data_vencimento))[0]
    const temAtraso = faturas.some(f => f.status === 'atrasada')

    /* Destaques: services flagged as destaque_loja or config_contratacao.destaque */
    const destaques = servicos.filter(s => s.contratacao_online && s.ativo && s.visivel_portal !== false && (s.destaque_loja || s.config_contratacao?.destaque))

    /* Recomendados: online services the associate hasn't contracted yet */
    const contratadoIds = new Set(
        contratos
            .filter(c =>
                c.status === 'ativo' ||
                c.status === 'pendente_adesao' ||
                c.status === 'pendente_mensalidade'
            )
            .flatMap(c => {
                const meta = c.metadata as { servico_ids?: string[] }
                return meta?.servico_ids || []
            }),
    )
    const recomendadosWithVisibility = servicos.filter(
        s => s.contratacao_online && s.ativo && !contratadoIds.has(s.id) && s.visivel_portal !== false
    )
    
    // Separamos quem é destaque pra não duplicar
    const recomendados = recomendadosWithVisibility.filter(s => !s.destaque_loja && !s.config_contratacao?.destaque)

    /* Sempre Juntos State */
    const rawCpf = usePortalStore(s => s.cpf) || associado?.cpf
    const [sjPontos, setSjPontos] = useState(0)
    const [sjDisponivel, setSjDisponivel] = useState(false)
    const [sjLoading, setSjLoading] = useState(true)
    const [sjCheckingIn, setSjCheckingIn] = useState(false)

    const { data: realExtratoDashboard } = useQuery({
        queryKey: ['portal_pontos_extrato_dashboard', associado?.id],
        queryFn: async () => {
            if (!associado?.id) return []
            const { data, error } = await supabase
                .schema('veicular_system')
                .from('veic_pontos_extrato')
                .select('*')
                .eq('associado_id', associado.id)
                .order('created_at', { ascending: false })
            if (error) throw error
            return data
        },
        enabled: !!associado?.id
    })

    const { data: saldoDashboard, refetch: refetchSaldoDash } = useQuery({
        queryKey: ['portal_pontos_saldo_dashboard', associado?.id],
        queryFn: async () => {
            if (!associado?.id) return 0
            const { data, error } = await supabase
                .schema('veicular_system')
                .rpc('get_saldo_pontos_associado', { p_associado_id: associado.id })
            if (error) throw error
            return data || 0
        },
        enabled: !!associado?.id
    })

    useEffect(() => {
        if (realExtratoDashboard) {
            const ultimosCheckins = realExtratoDashboard.filter((i: any) => i.tipo === 'checkin')
            if (ultimosCheckins.length === 0) {
                setSjDisponivel(true)
            } else {
                const lastDate = new Date(ultimosCheckins[0].created_at)
                const diffMs = new Date().getTime() - lastDate.getTime()
                const diffDays = diffMs / (1000 * 60 * 60 * 24)
                setSjDisponivel(diffDays >= 30)
            }
            setSjLoading(false)
        }
    }, [realExtratoDashboard])

    const handleSempreJuntosCheckin = async () => {
        if (!associado?.id || sjCheckingIn) return
        setSjCheckingIn(true)
        try {
            const { data, error } = await supabase
                .schema('veicular_system')
                .rpc('fazer_checkin_fidelidade', { p_associado_id: associado.id })

            if (error) throw error
            
            if (data?.success === false) {
                alert(data.message)
            } else if (data?.success === true) {
                refetchSaldoDash()
                alert('Parabéns! Você ganhou 10 pontos!')
            }
        } catch (error) {
            console.error('Erro no checkin', error)
            alert('Não foi possível processar o check-in.')
        } finally {
            setSjCheckingIn(false)
        }
    }

    return (
        <div className="p-5 space-y-8 animate-fade-in max-w-lg mx-auto pb-10">
            {/* Greeting */}
            <div className="pt-4 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-extrabold text-[#061C37] tracking-tight">Olá, {PORTAL_ASSOCIADO_NOME} 👋</h1>
                    <p className="text-gray-400 text-sm font-medium">Sua proteção em um só lugar</p>
                </div>
                <div className="w-12 h-12 bg-white rounded-2xl shadow-lg border border-gray-50 flex items-center justify-center text-[#003E8A]">
                    <ShieldCheck size={28} />
                </div>
            </div>

            {/* Alert */}
            {temAtraso && (
                <div className="flex items-start gap-4 p-5 rounded-[2rem] bg-red-50 border border-red-100 shadow-sm animate-pulse-subtle">
                    <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center text-white flex-shrink-0 shadow-lg shadow-red-200">
                        <AlertTriangle size={20} />
                    </div>
                    <div>
                        <p className="text-sm font-bold text-red-900">Regularize sua proteção</p>
                        <p className="text-xs text-red-700/80 mt-1 leading-relaxed">Você possui faturas em atraso. Efetue o pagamento para evitar a suspensão dos seus benefícios.</p>
                    </div>
                </div>
            )}

            {/* Cards */}
            <div className="grid grid-cols-2 gap-4">
                <div className="p-6 rounded-[2.5rem] bg-gradient-to-br from-[#003E8A] to-[#002B61] text-white shadow-2xl shadow-[#003E8A]/30 relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
                    <ShieldCheck size={32} className="mb-4 text-[#00A344] drop-shadow-md" />
                    <p className="text-4xl font-black">{contratosAtivos}</p>
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-60 mt-1">Proteções Ativas</p>
                </div>

                <div className="p-6 rounded-[2.5rem] bg-white border border-gray-100 shadow-xl shadow-gray-200/50 flex flex-col justify-between">
                    <Receipt size={32} className="text-[#003E8A] mb-4 opacity-40" />
                    {proximaFatura ? (
                        <div>
                            <p className="text-2xl font-black text-[#061C37] tracking-tighter">{formatCurrency(proximaFatura.valor)}</p>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Vence {formatDate(proximaFatura.data_vencimento)}</p>
                        </div>
                    ) : (
                        <div>
                            <p className="text-lg font-black text-[#00A344]">Tudo em dia!</p>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Nenhuma pendência</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Sempre Juntos */}
            <div 
                onClick={() => navigate('/portal/pontos')}
                className="p-6 rounded-[2.5rem] bg-gradient-to-br from-pink-500 to-rose-600 text-white shadow-xl shadow-pink-500/20 relative overflow-hidden group cursor-pointer active:scale-[0.98] transition-all"
            >
                <div className="absolute -right-4 -top-4 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-125 transition-transform duration-700" />
                <div className="relative z-10 flex flex-col justify-between h-full">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                                <Heart size={20} className="text-white fill-current animate-pulse-subtle" />
                            </div>
                            <span className="font-black tracking-tight text-lg drop-shadow-sm">Sempre Juntos</span>
                        </div>
                        <div className="text-right">
                            <span className="text-3xl font-black drop-shadow-md">{saldoDashboard || 0}</span>
                            <span className="text-[10px] font-black uppercase tracking-widest bg-white/20 px-2 py-0.5 rounded-full ml-1">pts</span>
                        </div>
                    </div>
                    {sjLoading ? (
                        <div className="h-10 bg-white/20 rounded-2xl animate-pulse" />
                    ) : sjDisponivel ? (
                        <button
                            onClick={(e) => {
                                e.stopPropagation()
                                handleSempreJuntosCheckin()
                            }}
                            disabled={sjCheckingIn}
                            className="w-full py-3.5 bg-white text-rose-600 font-black rounded-2xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {sjCheckingIn ? (
                                <div className="w-5 h-5 border-2 border-rose-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>Garantir meus 10 pontos <Sparkles size={16} /></>
                            )}
                        </button>
                    ) : (
                        <div className="w-full py-3 bg-white/10 border border-white/20 rounded-2xl flex items-center justify-center gap-2">
                            <ShieldCheck size={16} className="text-white opacity-80" />
                            <span className="text-xs font-bold text-white opacity-90">Você já garantiu seus pontos este mês!</span>
                        </div>
                    )}
                </div>
            </div>

            {/* 🔥 Destaques */}
            {destaques.length > 0 && (
                <DestaqueCarousel
                    servicos={destaques}
                    onContract={setSelectedServico}
                />
            )}

            {/* 📋 Recomendados */}
            {recomendados.length > 0 && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                        <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
                            <Sparkles size={14} className="text-[#00A344]" /> Recomendados
                        </p>
                        <Link to="/portal/loja" className="text-xs text-[#003E8A] font-black hover:underline flex items-center gap-1 group">
                            Ver todos <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                    <div className="space-y-3">
                        {recomendados.slice(0, 3).map(s => (
                            <div
                                key={s.id}
                                className="flex items-center gap-4 p-4 rounded-3xl bg-white border border-gray-100 shadow-sm
                                    hover:border-[#003E8A]/30 hover:shadow-xl transition-all cursor-pointer group"
                                onClick={() => setSelectedServico(s)}
                            >
                                <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center flex-shrink-0 overflow-hidden group-hover:shadow-lg transition-all">
                                    {s.imagem_url ? (
                                        <img src={s.imagem_url} alt={s.nome} className="w-full h-full object-cover" />
                                    ) : (
                                        <ShoppingBag size={22} className="text-[#003E8A]/40" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-sm text-[#061C37] truncate group-hover:text-[#003E8A] transition-colors">{s.nome}</p>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        <p className="text-[11px] font-bold text-gray-400">
                                            Por apenas <span className="text-[#00A344]">{formatCurrency(s.valor_mensal)}</span>/mês
                                        </p>
                                        {!!s.pontos_adesao && s.pontos_adesao > 0 && (
                                            <span className="text-[9px] font-black bg-rose-50 text-rose-500 px-1.5 py-0.5 rounded border border-rose-100 flex items-center gap-0.5">
                                                <Heart size={8} className="fill-current" /> +{s.pontos_adesao} PTS
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-300 group-hover:bg-[#003E8A] group-hover:text-white transition-all">
                                    <ArrowRight size={18} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Quick Actions */}
            <div className="space-y-4">
                <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] px-1">Atalhos</p>
                <div className="grid grid-cols-1 gap-3">
                    <Link to="/portal/contratos"
                        className="flex items-center justify-between p-5 rounded-3xl bg-white border border-gray-50 shadow-sm hover:shadow-lg transition-all group">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-[#003E8A]/5 flex items-center justify-center text-[#003E8A] group-hover:scale-110 transition-transform">
                                <FileText size={24} />
                            </div>
                            <div>
                                <p className="font-bold text-sm text-[#061C37]">Meus Contratos</p>
                                <p className="text-xs font-medium text-gray-400">Ver detalhes das proteções</p>
                            </div>
                        </div>
                        <ArrowRight size={20} className="text-gray-300 group-hover:text-[#003E8A] transition-colors" />
                    </Link>
                    <Link to="/portal/faturas"
                        className="flex items-center justify-between p-5 rounded-3xl bg-white border border-gray-50 shadow-sm hover:shadow-lg transition-all group">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-[#00A344]/5 flex items-center justify-center text-[#00A344] group-hover:scale-110 transition-transform">
                                <Receipt size={24} />
                            </div>
                            <div>
                                <p className="font-bold text-sm text-[#061C37]">Minhas Faturas</p>
                                <p className="text-xs font-medium text-gray-400">Segunda via e histórico</p>
                            </div>
                        </div>
                        <ArrowRight size={20} className="text-gray-300 group-hover:text-[#003E8A] transition-colors" />
                    </Link>
                </div>
            </div>

            {/* 📰 Notícias */}
            {noticias && noticias.length > 0 && (
                <NoticiasSection noticias={noticias} />
            )}

            {/* Contracting Modal */}
            {selectedServico && (
                <ContractingModal
                    servico={selectedServico}
                    onClose={() => setSelectedServico(null)}
                />
            )}
        </div>
    )
}

function DestaqueCarousel({ servicos, onContract }: { servicos: Servico[]; onContract: (s: Servico) => void }) {
    const scrollRef = useRef<HTMLDivElement>(null)

    return (
        <div className="space-y-4">
            <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2 px-1">
                <Star size={14} className="text-amber-500" /> Em Destaque
            </p>
            <div
                ref={scrollRef}
                className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-5 px-5 pb-4"
                style={{ scrollbarWidth: 'none' }}
            >
                {servicos.map(s => (
                    <div
                        key={s.id}
                        onClick={() => onContract(s)}
                        className="flex-shrink-0 w-72 snap-start rounded-[2.5rem] overflow-hidden shadow-xl
                            bg-white border border-gray-100 min-h-[160px]
                            hover:shadow-2xl transition-all cursor-pointer relative group"
                    >
                        {/* Background Image with Overlay */}
                        {s.imagem_url ? (
                            <div className="absolute inset-0 z-0">
                                <img src={s.imagem_url} alt={s.nome} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#061C37] via-[#061C37]/60 to-transparent" />
                            </div>
                        ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-[#003E8A] to-[#002B61] z-0" />
                        )}
                        
                        <div className="p-7 space-y-3 relative z-10 flex flex-col h-full justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="px-3 py-1 bg-amber-400 text-white rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg">
                                        {s.destaque_loja ? 'Mais Popular' : 'Destaque'}
                                    </div>
                                    {!!s.pontos_adesao && s.pontos_adesao > 0 && (
                                        <div className="px-3 py-1 bg-rose-500 text-white rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg flex items-center gap-1">
                                            <Heart size={10} className="fill-current" /> +{s.pontos_adesao} PTS
                                        </div>
                                    )}
                                </div>
                                <h3 className="text-xl font-black text-white leading-tight drop-shadow-sm">{s.nome}</h3>
                                <p className="text-[11px] text-white/70 font-bold line-clamp-2 leading-relaxed mt-1">{s.descricao}</p>
                            </div>
                            
                            <div className="flex items-center justify-between pt-4">
                                <div>
                                    <p className="text-2xl font-black text-white tracking-tighter">{formatCurrency(s.valor_mensal)}</p>
                                    <p className="text-[9px] font-black text-white/40 uppercase tracking-[0.2em]">por mês</p>
                                </div>
                                <div className="px-5 py-3 rounded-2xl bg-[#00A344] text-white text-xs font-black shadow-lg shadow-[#00A344]/30 hover:scale-105 active:scale-95 transition-all">
                                    {s.texto_botao || 'Contratar'}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

const NOTICIA_ICONS: Record<string, typeof Newspaper> = {
    noticia: Newspaper,
    informacao: Info,
    aviso: Megaphone,
}
const NOTICIA_COLORS: Record<string, { bg: string; text: string; border: string; icon: string }> = {
    noticia: { bg: 'bg-blue-50/50', text: 'text-blue-800', border: 'border-blue-100', icon: 'text-blue-600' },
    informacao: { bg: 'bg-emerald-50/50', text: 'text-emerald-800', border: 'border-emerald-100', icon: 'text-emerald-600' },
    aviso: { bg: 'bg-amber-50/50', text: 'text-amber-800', border: 'border-amber-100', icon: 'text-amber-600' },
}

function NoticiasSection({ noticias }: { noticias: NoticiaPortal[] }) {
    return (
        <div className="space-y-4 pb-4">
            <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] px-1">Notícias</p>
            <div className="space-y-3">
                {noticias.map(n => {
                    const Icon = NOTICIA_ICONS[n.tipo] || Newspaper
                    const colors = NOTICIA_COLORS[n.tipo] || NOTICIA_COLORS.noticia!

                    return (
                        <div
                            key={n.id}
                            className={`p-5 rounded-3xl border ${colors.bg} ${colors.border} space-y-2 group hover:shadow-md transition-all`}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-xl bg-white flex items-center justify-center shadow-sm ${colors.icon}`}>
                                    <Icon size={16} />
                                </div>
                                <h4 className={`text-sm font-bold ${colors.text}`}>{n.titulo}</h4>
                            </div>
                            <p className={`text-xs ${colors.text} opacity-70 leading-relaxed pl-11`}>{n.conteudo}</p>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
